import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in local storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {number} [maxProducts] - Optional max products limit override.
   */
  static addProduct(productId, maxProducts = this.#MAX_PRODUCTS) {
    let viewedProducts = this.getProducts();
    const storageLimit = Math.max(1, Number(maxProducts) || this.#MAX_PRODUCTS);

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, storageLimit);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from local storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

class RecentlyViewedProducts extends Component {
  /**
   * The observer for the recently viewed products
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewedProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    const { sectionId, currentProductId } = this.dataset;
    if (!sectionId) return null;

    const maxProducts = Number.parseInt(this.dataset.maxProducts ?? '4', 10) || 4;
    const viewedProducts = RecentlyViewed.getProducts()
      .filter((productId) => productId !== currentProductId)
      .slice(0, maxProducts);

    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    return sectionRenderer.getSectionHTML(sectionId, false, url);
  }

  /**
   * Load the recently viewed products markup into the current component.
   */
  #loadRecentlyViewedProducts() {
    const { recentlyViewedPerformed, sectionId } = this.dataset;
    const id = this.id;

    if (!sectionId || !id) {
      throw new Error('Section ID and an ID attribute are required');
    }

    if (recentlyViewedPerformed === 'true') {
      return;
    }

    this.#getRecentlyViewedProductsMarkup()
      .then((markup) => {
        if (!markup) {
          this.#hide();
          return;
        }

        const parsedMarkup = new DOMParser().parseFromString(markup, 'text/html');
        const recentlyViewedProducts = parsedMarkup.querySelector(
          `recently-viewed-products[id="${id}"]`
        );

        if (!recentlyViewedProducts) {
          this.#hide();
          return;
        }

        const productItems = recentlyViewedProducts.querySelectorAll('.resource-list__item');
        if (!productItems.length) {
          this.#hide();
          return;
        }

        this.dataset.recentlyViewedPerformed = 'true';
        this.dataset.hasRecentlyViewedProducts = 'true';
        morph(this, recentlyViewedProducts);
      })
      .catch(() => {
        this.#hide();
      });
  }

  /**
   * Hide the section when no recently viewed products can be rendered.
   */
  #hide() {
    this.classList.add('hidden');
    this.dataset.hasRecentlyViewedProducts = 'false';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
